'use client';

import type { RefObject } from 'react';
import { useCallback, useRef, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import type { GridState } from 'ag-grid-community';
import type { ExternalFilters } from '../utils/table-persistence.utils';
import {
  createUserTableSuffix,
  TABLE_STATE_PREFIX,
  createApplicationTableSuffix,
} from '../utils/table-persistence.utils';
import { tablePersistenceStore } from '../stores/tablePersistenceStore';

export type UseTablePersistenceProps = {
  tableId: string;
  applicationId?: string;
  userId?: string;
  externalFilters?: ExternalFilters;
  onExternalFiltersRestore?: (filters: ExternalFilters) => void;
};

export type TablePersistenceReturn = {
  savedState?: GridState | null;
  handleSaveState: (data: GridState) => void;
  hasInitialized: RefObject<boolean>;
  resetPersistence: () => void;
};

export function useTablePersistence({
  tableId,
  applicationId,
  userId: userIdInput,
  externalFilters,
  onExternalFiltersRestore,
}: UseTablePersistenceProps): TablePersistenceReturn {
  const storeHasHydrated = useStore(
    tablePersistenceStore,
    (state) => state._hasHydrated,
  );

  const userId = userIdInput?.trim() || null;

  const createStorageKey = useCallback((): string | null => {
    if (!userId) return null;

    const userSuffix = createUserTableSuffix(userId as string);
    let key = `${TABLE_STATE_PREFIX}${userSuffix}_${tableId}`;

    if (applicationId) {
      key += `_${createApplicationTableSuffix(applicationId)}`;
    }

    return key;
  }, [tableId, applicationId, userId]);

  const storageKey = createStorageKey();
  const [synchronousInitialState] = useState(() =>
    storageKey && storeHasHydrated && !onExternalFiltersRestore
      ? {
          storageKey,
          state: tablePersistenceStore
            .getState()
            .getTablePersistenceState(storageKey, tableId),
        }
      : null,
  );
  const fetchedInitialStateRef = useRef<boolean>(
    synchronousInitialState !== null,
  );
  const [savedState, setSavedState] = useState<GridState | null | undefined>(
    synchronousInitialState?.state,
  );
  const hasInitialized = useRef<boolean>(synchronousInitialState !== null);
  const currentStorageKeyRef = useRef<string | null>(
    synchronousInitialState?.storageKey ?? null,
  );
  const [trackedStorageKey, setTrackedStorageKey] = useState(storageKey);

  if (storageKey !== trackedStorageKey) {
    setTrackedStorageKey(storageKey);
    currentStorageKeyRef.current = storageKey;
    fetchedInitialStateRef.current = false;
    hasInitialized.current = false;
    setSavedState(null);
  }

  const getSavedState = useCallback((): GridState | null => {
    if (!storageKey || !storeHasHydrated) return null;

    if (onExternalFiltersRestore && !fetchedInitialStateRef.current) {
      const externalFiltersFromStore = tablePersistenceStore
        .getState()
        .getExternalFilters(storageKey, tableId);

      if (externalFiltersFromStore) {
        onExternalFiltersRestore(externalFiltersFromStore);
      }
    }

    fetchedInitialStateRef.current = true;

    return tablePersistenceStore
      .getState()
      .getTablePersistenceState(storageKey, tableId);
  }, [storageKey, tableId, onExternalFiltersRestore, storeHasHydrated]);

  useEffect(() => {
    if (!hasInitialized.current && storeHasHydrated) {
      if (storageKey === null) {
        hasInitialized.current = true;
        setSavedState(null);

        return;
      }

      const initialState = getSavedState();

      setSavedState(initialState);
      hasInitialized.current = true;
    }
  }, [getSavedState, storageKey, storeHasHydrated]);

  const saveStateToStore = useCallback(
    (data: GridState) => {
      if (!storageKey) {
        console.debug(
          `Skipping table state save for ${tableId}: no valid user`,
        );

        return;
      }

      tablePersistenceStore
        .getState()
        .saveTablePersistenceState(storageKey, tableId, data, externalFilters);
    },
    [storageKey, tableId, externalFilters],
  );

  const resetPersistence = useCallback(() => {
    try {
      if (storageKey) {
        tablePersistenceStore
          .getState()
          .removeTablePersistenceState(storageKey);
        console.info(`Reset table state for ${tableId}`);
      }

      setSavedState(null);
      fetchedInitialStateRef.current = false;
      // Force re-initialization on next render
      currentStorageKeyRef.current = null;
    } catch (error) {
      console.warn(`Failed to reset table state for ${tableId}:`, error);
    }
  }, [storageKey, tableId]);

  return {
    savedState,
    handleSaveState: saveStateToStore,
    hasInitialized,
    resetPersistence,
  };
}
